'use client';

import { useToast } from '@common/hooks';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { AuthApi } from '@/api';

import {
  invalidateExceptCommentLists,
  patchCommentInAllLists,
  reconcileCommentListsAfterChange,
} from './commentListSync';

interface UsePutCommentMutationParams {
  /** 수정 대상 댓글 id */
  commentId: number;
  /** 성공 시 상위에서 모드 종료와 스크롤 복원을 처리 */
  onSuccess?: () => void;
}

// 댓글 수정 API와 목록 낙관 반영을 함께 처리한다.
export const usePutCommentMutation = ({
  commentId,
  onSuccess,
}: UsePutCommentMutationParams) => {
  const { toast, toastError } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (contents: string) =>
      AuthApi['put:/wello/v2/auth/wello-comment/{welloCommentId}']({
        path: {
          welloCommentId: commentId,
        },
        body: {
          contents,
        },
      }),
    onSuccess: (_, contents) => {
      toast({ type: 'success', message: '댓글 수정완료' });

      // 백엔드 목록 반영이 늦어도 수정 내용이 즉시 보이도록 캐시를 먼저 고친다.
      patchCommentInAllLists(queryClient, commentId, { contents });

      onSuccess?.();

      void invalidateExceptCommentLists(queryClient);
      // 반영이 확인된 뒤에만 목록을 refetch한다. 미확인이면 낙관 상태 유지.
      void reconcileCommentListsAfterChange({
        queryClient,
        commentId,
        checkReflected: (comment) => comment?.contents === contents,
      });
    },
    onError: (error) => {
      toastError({ error });
    },
  });
};
